import React from "react";
import { Modal } from "react-bootstrap";
import ChecklistRadioOptions from "../../Common/ChecklistRadioOptions";
import Resources from "../../../types/Resources";
import BeefGasChecklistFormState from "../../../types/forms/Permits/Conditions/BeefGasChecklistFormState";
import { BEEF_GAS_CHECKLIST_VERSION_2026_02 } from "../../../types/permits/conditions/BeefGasChecklist";

type BeefGasChecklistModalProps = {
    checklistVersion: number;
    resources: Resources;
    onChange: (fn: (prevValue: BeefGasChecklistFormState) => BeefGasChecklistFormState) => void;
    show: boolean;
    onHide: () => void;
    formState: BeefGasChecklistFormState;
    canManageContent: boolean;
};

function BeefGasChecklistModal({ checklistVersion, resources, onChange, show, onHide, formState, canManageContent }: BeefGasChecklistModalProps) {
    function changeHandler(key: keyof BeefGasChecklistFormState, value: any) {
        onChange((prevValue) => {
            const updatedFormState: BeefGasChecklistFormState = {
                ...prevValue,
                [key]: { ...prevValue[key], value }
            };

            return updatedFormState;
        });
    }

    return (
        <Modal show={show} onHide={onHide} size="xl">
            <Modal.Header closeButton>
                <h1 className="h5">{resources.get("ChecklistBeefGas")}</h1>
            </Modal.Header>
            <Modal.Body>
                {checklistVersion === BEEF_GAS_CHECKLIST_VERSION_2026_02 ? (
                    <ChecklistVersion202602
                        canManageContent={canManageContent}
                        changeHandler={changeHandler}
                        formState={formState}
                        resources={resources}
                    />
                ) : (
                    <ChecklistVersion0
                        canManageContent={canManageContent}
                        changeHandler={changeHandler}
                        formState={formState}
                        resources={resources}
                    />
                )}
            </Modal.Body>
        </Modal>
    );
}

type ChecklistProps = {
    resources: Resources;
    formState: BeefGasChecklistFormState;
    canManageContent: boolean;
    changeHandler: (key: keyof BeefGasChecklistFormState, value: any) => void;
};

function ChecklistVersion0({ resources, formState, changeHandler, canManageContent }: ChecklistProps) {
    return (
        <ul className="list-unstyled">
            <li className="m-2">
                <ChecklistRadioOptions
                    id="needs-fresh-air-device"
                    labelText={resources.get("NeedsFreshAirDevice")}
                    onChange={(updatedValue) => changeHandler("needsFreshAirDevice", updatedValue)}
                    resources={resources}
                    value={formState.needsFreshAirDevice.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <ChecklistRadioOptions
                    id="is-fresh-air-mask-for-everyone"
                    labelText={resources.get("IsFreshAirMaskForEveryone")}
                    onChange={(updatedValue) => changeHandler("isFreshAirMaskForEveryone", updatedValue)}
                    resources={resources}
                    value={formState.isFreshAirMaskForEveryone.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <ChecklistRadioOptions
                    id="is-portable-gas-measurements-alarm-available-for-everyone"
                    labelText={resources.get("IsPortableGasMeasurementAlarmAvailableForEveryone")}
                    onChange={(updatedValue) =>
                        changeHandler("isPortableGasMeasurementAlarmAvailableForEveryone", updatedValue)
                    }
                    resources={resources}
                    value={formState.isPortableGasMeasurementAlarmAvailableForEveryone.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <ChecklistRadioOptions
                    id="is-continuos-gas-measurements-in-place"
                    labelText={resources.get("IsContinousGasMeasurementsInPlace")}
                    onChange={(updatedValue) =>
                        changeHandler("isContinousGasMeasurementsInPlace", updatedValue)
                    }
                    resources={resources}
                    value={formState.isContinousGasMeasurementsInPlace.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <ChecklistRadioOptions
                    id="is-continuos-ventilation-in-place"
                    labelText={resources.get("IsContinuousVentilationInPlace")}
                    onChange={(updatedValue) => changeHandler("isContinuousVentilationInPlace", updatedValue)}
                    resources={resources}
                    value={formState.isContinuousVentilationInPlace.value}
                    readOnly={!canManageContent}
                />
            </li>
        </ul>
    );
}

function ChecklistVersion202602({ resources, formState, changeHandler, canManageContent }: ChecklistProps) {
    return (
        <ul className="list-unstyled">
            <li className="m-2">
                <ChecklistRadioOptions
                    id="is-fresh-air-mask-needed-during-work"
                    labelText={resources.get("IsFreshAirMaskNeededDuringWork")}
                    onChange={(updatedValue) => changeHandler("isFreshAirMaskNeededDuringWork", updatedValue)}
                    resources={resources}
                    value={formState.isFreshAirMaskNeededDuringWork.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <ChecklistRadioOptions
                    id="is-continuous-ventilation-needed"
                    labelText={resources.get("IsContinuousVentilationNeeded")}
                    onChange={(updatedValue) => changeHandler("isContinuousVentilationNeeded", updatedValue)}
                    resources={resources}
                    value={formState.isContinuousVentilationNeeded.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <ChecklistRadioOptions
                    id="is-oxygen-level-gas-meter-available"
                    labelText={resources.get("IsOxygenLevelGasMeterAvailable")}
                    onChange={(updatedValue) => changeHandler("isOxygenLevelGasMeterAvailable", updatedValue)}
                    resources={resources}
                    value={formState.isOxygenLevelGasMeterAvailable.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <ChecklistRadioOptions
                    id="is-continuous-gas-measurements-in-place-for-entirety-of-work"
                    labelText={resources.get("IsContinousGasMeasurementsInPlaceForEntirityOfWork")}
                    onChange={(updatedValue) => changeHandler("isContinousGasMeasurementsInPlaceForEntirityOfWork", updatedValue)}
                    resources={resources}
                    value={formState.isContinousGasMeasurementsInPlaceForEntirityOfWork.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <ChecklistRadioOptions
                    id="is-wind-direction-considered-during-work"
                    labelText={resources.get("IsWindDirectionConsideredDuringWork")}
                    onChange={(updatedValue) => changeHandler("isWindDirectionConsideredDuringWork", updatedValue)}
                    resources={resources}
                    value={formState.isWindDirectionConsideredDuringWork.value}
                    readOnly={!canManageContent}
                />
            </li>
        </ul>
    );
}

export default BeefGasChecklistModal;

